import { Fragment, type ReactNode } from "react";
import { Button as BaseButton } from "@base-ui/react/button";
import {
  IconBan,
  IconDotsVertical,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import Card from "../UI/Card";
import DropdownMenu from "../UI/DropdownMenu";
import IconButton from "../UI/IconButton";
import Text from "../UI/Text";
import Title from "../UI/Title";
import Tooltip from "../UI/Tooltip";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import styles from "./MandatoryDiagnostics.module.css";

export interface DiagnosticEntry {
  tag: string;
  /** Libellé court (DPE, Gaz, ERP…). */
  label: string;
  /** Nom complet du document. */
  name: string;
  /** Règle qui rend le diagnostic exigible ou non ; absente pour une dispense manuelle. */
  reason?: string;
}

interface MandatoryDiagnosticsProps {
  /** Diagnostics exigibles sans aucun document. */
  missing: DiagnosticEntry[];
  /** Cartes des documents de diagnostic déposés. */
  provided: ReactNode[];
  /** Diagnostics non exigibles d'après le bien (`reason`) ou dispensés à la main. */
  notRequired: DiagnosticEntry[];
  /** Diagnostics exigibles couverts par un document en cours de validité. */
  providedCount: number;
  /** Diagnostics exigibles, fournis ou non. */
  requiredCount: number;
  /** « ce logement », « ce local » ou « cette colocation ». */
  subjectLabel: string;
  profileBailleurId?: number;
  onAdd: (tag: string) => void;
  onSetNotApplicable: (tag: string, notApplicable: boolean) => void;
  /** « list » : en-tête et pied seuls, le tableau des diagnostics est passé en `children`. */
  layout?: "cards" | "list";
  children?: ReactNode;
}

/**
 * Bloc « Diagnostics obligatoires » de l'onglet Documents d'un bien : les
 * diagnostics à fournir d'après ses caractéristiques, ceux déjà déposés, et
 * ceux dont il est dispensé, avec l'avancement en tête.
 */
export default function MandatoryDiagnostics({
  missing,
  provided,
  notRequired,
  providedCount,
  requiredCount,
  subjectLabel,
  profileBailleurId,
  onAdd,
  onSetNotApplicable,
  layout = "cards",
  children,
}: MandatoryDiagnosticsProps) {
  const content = (
    <>
      <div className={styles.header}>
        <div className={styles.heading}>
          <Title variant="hecto" weight="bold">
            Diagnostics obligatoires
          </Title>
          <Text color="muted">
            Exigés pour louer ce bien, d'après ses caractéristiques
          </Text>
        </div>
        {requiredCount > 0 && (
          <div className={styles.progress}>
            <div
              className={styles.segments}
              role="img"
              aria-label={`${providedCount} diagnostics fournis sur ${requiredCount}`}
            >
              {Array.from({ length: requiredCount }, (_, i) => (
                <span
                  key={i}
                  className={classnames(
                    styles.segment,
                    i < providedCount && styles.segmentFilled,
                  )}
                />
              ))}
            </div>
            <Text as="span">
              <Text as="span" weight="bold">
                {providedCount}
              </Text>{" "}
              sur {requiredCount} fournis
            </Text>
          </div>
        )}
      </div>

      {layout === "list" && children}

      {layout === "cards" && missing.length > 0 && (
        <section className={styles.section}>
          <Text variant="deci" weight="bold" className={styles.eyebrow}>
            À fournir
          </Text>
          <div className={styles.missingGrid}>
            {missing.map((entry) => (
              <div key={entry.tag} className={styles.missingCard}>
                <IconButton
                  icon={IconPlus}
                  variant="primary"
                  label={`Ajouter : ${entry.name}`}
                  requiresWrite
                  profileId={profileBailleurId}
                  onClick={() => onAdd(entry.tag)}
                />
                <Tooltip content={entry.reason ?? entry.name}>
                  <BaseButton
                    className={styles.missingBody}
                    onClick={() => onAdd(entry.tag)}
                  >
                    <Text as="span" weight="bold">
                      {entry.label}
                    </Text>
                  </BaseButton>
                </Tooltip>
                <DropdownMenu
                  icon={IconDotsVertical}
                  label={`Actions : ${entry.name}`}
                  align="end"
                  className={styles.menu}
                >
                  <DropdownMenu.Item
                    icon={IconPlus}
                    label="Ajouter le document"
                    requiresWrite
                    profileId={profileBailleurId}
                    onClick={() => onAdd(entry.tag)}
                  />
                  <DropdownMenu.Item
                    icon={IconBan}
                    label="Ce bien n'est pas concerné"
                    requiresWrite
                    profileId={profileBailleurId}
                    onClick={() => onSetNotApplicable(entry.tag, true)}
                  />
                </DropdownMenu>
              </div>
            ))}
          </div>
        </section>
      )}

      {layout === "cards" && provided.length > 0 && (
        <section className={styles.section}>
          <Text variant="deci" weight="bold" className={styles.eyebrow}>
            Fournis
          </Text>
          <div className={styles.providedGrid}>{provided}</div>
        </section>
      )}

      {notRequired.length > 0 && (
        <Text as="div" color="muted" className={styles.footer}>
          Non requis pour {subjectLabel} :{" "}
          {notRequired.map((entry, i) => (
            <Fragment key={entry.tag}>
              {i > 0 && ", "}
              {entry.reason ? (
                <Tooltip content={entry.reason}>
                  <Text as="span" className={styles.hint}>
                    {entry.label}
                  </Text>
                </Tooltip>
              ) : (
                <Tooltip content="Dispensé à la main : cliquer pour redemander ce diagnostic">
                  <BaseButton
                    className={styles.manual}
                    aria-label={`${entry.name} : redemander`}
                    onClick={() => onSetNotApplicable(entry.tag, false)}
                  >
                    {entry.label}
                    <IconX size={16} stroke={1.5} />
                  </BaseButton>
                </Tooltip>
              )}
            </Fragment>
          ))}
        </Text>
      )}
    </>
  );
  if (layout === "list") {
    return <section className={styles.listSection}>{content}</section>;
  }
  return (
    <Card padding="md" className={styles.card}>
      {content}
    </Card>
  );
}
